import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { API, fileUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ChevronDown, Pencil, Plus } from "lucide-react";
import UserMenu from "@/components/UserMenu";

export default function BrandsOverview() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newBrand, setNewBrand] = useState({ name: "", tagline: "" });
  // Single hidden file input shared by every editable card.
  const avatarInputRef = useRef(null);
  const [uploadingBrand, setUploadingBrand] = useState(null);
  const [savingBrandId, setSavingBrandId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/brands");
        setBrands(data);
      } catch { toast.error(t("toasts.load_failed_brands") || "Could not load brands"); }
      finally { setLoading(false); }
    })();
  }, [t]);

  const activate = (id) => {
    localStorage.setItem("bh_active_brand", id);
    navigate("/");
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/brands", newBrand);
      toast.success(t("overview.created"));
      setShowCreate(false);
      setNewBrand({ name: "", tagline: "" });
      activate(data.id);
    } catch { toast.error(t("overview.create_failed")); }
  };

  // Open the file picker for a specific brand. Stops the parent-card click
  // so we don't navigate into the workspace by accident.
  const openAvatarPicker = (brand, e) => {
    e.stopPropagation();
    e.preventDefault();
    setUploadingBrand(brand);
    // Reset value so re-picking the same file still fires onChange.
    if (avatarInputRef.current) {
      avatarInputRef.current.value = "";
      avatarInputRef.current.click();
    }
  };

  const onAvatarFile = async (e) => {
    const file = e.target.files?.[0];
    const brand = uploadingBrand;
    setUploadingBrand(null);
    if (!file || !brand) return;
    if (!/^image\/(png|jpe?g|webp|svg\+xml)$/.test(file.type)) {
      toast.error(t("overview.avatar_bad_type") || "Only PNG, JPG, WEBP or SVG allowed");
      return;
    }
    setSavingBrandId(brand.id);
    try {
      // 1) Upload the raw file against this brand's bucket.
      const token = localStorage.getItem("bh_token");
      const fd = new FormData();
      fd.append("file", file);
      const resp = await fetch(`${API}/upload?brand_id=${brand.id}`, {
        method: "POST",
        body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!resp.ok) throw new Error("upload failed");
      const { file_id } = await resp.json();
      // 2) Link the newly uploaded asset as the brand's logo. The Logo page
      //    reads the same `logo_file_id` so the two stay in sync.
      await api.put(`/brands/${brand.id}`, { logo_file_id: file_id });
      // 3) Optimistically patch the card so the new image shows immediately.
      setBrands((bs) => bs.map((b) => (b.id === brand.id ? { ...b, logo_file_id: file_id } : b)));
      toast.success(t("overview.avatar_updated") || "Logo updated");
    } catch {
      toast.error(t("overview.avatar_failed") || "Could not update logo");
    } finally {
      setSavingBrandId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#f3f3f1] text-[#1a1a1a]">
      {/* Top bar — click opens the global user menu (Profile Settings / Log out).
          UserMenu's root is `inline-block w-full` so we wrap it in a `w-fit`
          shrink-to-content container; otherwise `justify-end` on the flex row
          has nothing to push against and the trigger stays glued to the left. */}
      <header className="w-full px-6 md:px-10 pt-8 pb-6 flex items-center justify-end">
        <div className="w-fit">
          <UserMenu align="end" side="bottom">
            <button
              data-testid="overview-user-menu"
              className="flex items-center gap-3 rounded-md pl-3 pr-2 py-1.5 hover:bg-white transition-colors"
            >
              <div className="text-right leading-tight">
                <div className="text-[11px] text-gray-500">{t("user_menu.logged_in_as")}</div>
                <div className="text-[13px] font-medium">{user?.name || user?.email}</div>
              </div>
              <ChevronDown className="w-4 h-4 text-gray-500" strokeWidth={1.75} />
            </button>
          </UserMenu>
        </div>
      </header>

      {/* Title */}
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="text-[11px] text-gray-500 mb-1">The Brand Hub</div>
        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight mb-2">{t("overview.title")}</h1>
        <p className="text-gray-500 text-base md:text-lg mb-10 max-w-xl">
          {t("overview.subtitle")}
        </p>
      </div>

      {/* Shared hidden file input used by every editable avatar overlay. */}
      <input
        ref={avatarInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/svg+xml"
        className="hidden"
        onChange={onAvatarFile}
        data-testid="overview-avatar-file"
      />

      {/* Grid */}
      <main className="max-w-6xl mx-auto px-6 md:px-10 pb-20">
        {loading ? (
          <div className="text-gray-500">{t("overview.loading")}</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="brands-grid">
            {brands.map((b) => (
              <BrandCard
                key={b.id}
                brand={b}
                saving={savingBrandId === b.id}
                onActivate={() => activate(b.id)}
                onEditAvatar={(e) => openAvatarPicker(b, e)}
              />
            ))}

            {/* Add card — same footprint as brand cards; label stays anchored
                at the bottom via `mt-auto` so the Plus and the title sit at
                the exact same y-coordinates as the avatar + name on real
                cards. Hover darkens the Plus so it does not vanish into the
                card's white hover background. */}
            <button
              data-testid="overview-add-brand"
              onClick={() => setShowCreate(true)}
              className="group text-left bg-transparent rounded-2xl p-6 border-2 border-dashed border-gray-300 hover:border-[#1a1a1a] hover:bg-white transition-all min-h-[200px] flex flex-col"
            >
              <div className="w-16 h-16 rounded-xl bg-white group-hover:bg-gray-100 shadow-sm flex items-center justify-center transition-colors">
                <Plus className="w-5 h-5" strokeWidth={1.75} />
              </div>
              <div className="mt-auto">
                <div className="font-display text-xl font-medium leading-tight mb-1">{t("overview.new_brand_card")}</div>
                <div className="text-[13px] text-gray-500">{t("overview.new_brand_card_sub")}</div>
              </div>
            </button>
          </div>
        )}
      </main>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="overview-create-dialog" className="bg-white">
          <DialogHeader><DialogTitle>{t("overview.create_title")}</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <Label>{t("overview.name_label")}</Label>
              <Input data-testid="overview-new-brand-name" required value={newBrand.name} onChange={(e) => setNewBrand({ ...newBrand, name: e.target.value })} className="mt-1 bg-white" />
            </div>
            <div>
              <Label>{t("overview.tagline_label")}</Label>
              <Input data-testid="overview-new-brand-tagline" value={newBrand.tagline} onChange={(e) => setNewBrand({ ...newBrand, tagline: e.target.value })} className="mt-1 bg-white" />
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="overview-create-brand-submit" className="rounded-full bg-black text-white hover:bg-black/90">
                <Plus className="w-4 h-4 mr-2" /> {t("overview.create_cta")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Single brand card — LinkedIn-style banner + overlapping avatar.
 * The outer wrapper is `group` so the pencil edit affordance only reveals
 * on hover for owners/managers. Clicking the pencil opens the avatar file
 * picker; clicking anywhere else activates the workspace.
 */
function BrandCard({ brand: b, saving, onActivate, onEditAvatar }) {
  const canEdit = b.role === "owner" || b.role === "manager";
  const avatarUrl = b.logo_file_id ? fileUrl(b.logo_file_id) : null;
  return (
    <div className="group relative" data-testid={`brand-card-${b.id}`}>
      <button
        onClick={onActivate}
        className="w-full h-full min-h-[200px] text-left bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5 border border-transparent hover:border-gray-200 flex flex-col"
      >
        {/* Banner — brand's primary colour (Settings → Appearance). */}
        <div
          className="h-10 w-full flex-shrink-0"
          style={{ background: b.color_header_bg || b.color_primary || "#3a372b" }}
          data-testid={`brand-banner-${b.id}`}
        />
        {/* Body: grows via flex-1; name + role tag anchor to the bottom with
            pb-6 so there is ~60px of air between the avatar and the title. */}
        <div className="flex-1 bg-white px-6 pb-6 flex items-end justify-between gap-3">
          <div className="font-display text-xl font-medium leading-tight truncate text-[#1a1a1a]">
            {b.name}
          </div>
          <span
            data-testid={`role-tag-${b.id}`}
            className="text-[9px] font-normal uppercase tracking-wider px-2 py-1 rounded bg-gray-100 text-gray-600 shrink-0"
          >
            {b.role}
          </span>
        </div>
      </button>

      {/* Avatar overlay — sibling of the activation button so it can be
          clicked independently without bubbling into `onActivate`. Position
          matches the card's left padding (24px) so top + left padding stay
          symmetrical; part of the avatar straddles the 40px banner. */}
      <div className="absolute left-6 top-6 w-16 h-16 rounded-xl overflow-hidden ring-2 ring-white shadow-sm pointer-events-none">
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt=""
            className="w-full h-full object-cover bg-white"
            data-testid={`brand-avatar-${b.id}`}
          />
        ) : (
          <div
            className="w-full h-full"
            style={{ background: b.color_accent || "#fe7f57" }}
            data-testid={`brand-avatar-${b.id}`}
          />
        )}
      </div>

      {canEdit && (
        <button
          type="button"
          onClick={onEditAvatar}
          disabled={saving}
          data-testid={`brand-avatar-edit-${b.id}`}
          aria-label="Edit logo"
          className="absolute left-6 top-6 w-16 h-16 rounded-xl flex items-center justify-center bg-black/45 opacity-0 group-hover:opacity-100 transition-opacity ring-2 ring-white"
        >
          <Pencil className="w-4 h-4 text-white" strokeWidth={1.75} />
        </button>
      )}
    </div>
  );
}
